import { isAfter, UTCDate } from '@/utils/dates';
import { AccountView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';

type HistoricalCashBalanceTypes = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
  targetDate: Date;
};

/**
 * Calculates the total cash balance across all accounts at a specific point in time.
 * This function works backwards from the current balance, subtracting transactions
 * that occurred after the target date to determine the historical balance.
 */
export const getHistoricalCashBalance = ({ accounts, baseCurrency, targetDate }: HistoricalCashBalanceTypes) => {
  if (!accounts || accounts.length === 0) {
    return {
      historicalBalanceInBaseCurrency: 0,
      formattedHistoricalBalance: getAmountWithCurrency({
        amount: 0,
        currency: baseCurrency,
        isAccountBalance: true,
      }),
    };
  }

  // Start with current balance across all accounts
  const currentTotalBalance = accounts.reduce((acc, account) => acc + account.baseCurrencyAvailable, 0);

  // Collect all transactions from all accounts
  const allTransactions = accounts.flatMap((account) =>
    (account.transactions || []).map(({ fspDate, baseCurrencyAmount, isCredit }) => ({
      fspDate,
      baseCurrencyAmount,
      isCredit,
    })),
  );

  // Filter transactions that occurred after the target date
  const transactionsAfterTargetDate = allTransactions.filter((transaction) => {
    if (!transaction.fspDate) return false;
    const transactionDate = new UTCDate(transaction.fspDate);
    return isAfter(transactionDate, targetDate);
  });

  // Calculate net cash flow that occurred after the target date
  // Using the same logic as getCurrentCashData for consistency
  const netCashFlowAfterTargetDate = transactionsAfterTargetDate.reduce((acc, transaction) => {
    const amountAbs = Math.abs(transaction.baseCurrencyAmount || 0);
    const signedByCreditDebit = transaction.isCredit ? -amountAbs : amountAbs;
    return acc + signedByCreditDebit;
  }, 0);

  // Calculate the net change in balance (same as getCurrentCashData)
  const netChangeInBalanceAfterTargetDate = -netCashFlowAfterTargetDate;

  // Historical balance = current balance - net change that happened after target date
  const historicalBalanceInBaseCurrency = currentTotalBalance - netChangeInBalanceAfterTargetDate;

  const formattedHistoricalBalance = getAmountWithCurrency({
    amount: historicalBalanceInBaseCurrency,
    currency: baseCurrency,
    isAccountBalance: true,
  });

  return {
    historicalBalanceInBaseCurrency,
    formattedHistoricalBalance,
  };
};
